// WHY (#996): read-only display of the request-level Roads / Packaging
// multiselects on the request details / drawer surface. Lives in a
// cargo / metadata section — separate from the route fields — so the
// data plane (Roads/Packaging) is visually distinct from the route
// plane (`route.from` / destinations).
//
// Historical inactive selections render with a muted background and a
// localized "(inactive)" suffix; active selections render with the
// standard chip styling.

import { useTranslation } from 'react-i18next';
import type { RequestDictionarySelectionView } from '../types.js';

interface ChipListProps {
  items: ReadonlyArray<RequestDictionarySelectionView>;
  testId: string;
  emptyLabel: string;
  inactiveSuffix: string;
}

function ChipList({ items, testId, emptyLabel, inactiveSuffix }: ChipListProps) {
  if (items.length === 0) {
    return (
      <span data-testid={`${testId}-empty`} style={{ color: 'var(--fg-3)', fontSize: 13 }}>
        {emptyLabel}
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((row) => (
        <span
          key={row.id}
          data-testid={`${testId}-chip-${row.id}`}
          data-inactive={!row.active ? 'true' : 'false'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 8px',
            border: '1px solid var(--border-1)',
            borderRadius: 12,
            fontSize: 13,
            background: !row.active ? 'var(--bg-inset)' : 'var(--bg-2)',
            color: !row.active ? 'var(--fg-3)' : 'var(--fg-1)',
          }}
        >
          {row.name}
          {!row.active ? ` ${inactiveSuffix}` : ''}
        </span>
      ))}
    </div>
  );
}

export function RequestDictionarySelectionsView({
  roads,
  packaging,
}: {
  roads: ReadonlyArray<RequestDictionarySelectionView>;
  packaging: ReadonlyArray<RequestDictionarySelectionView>;
}) {
  const { t } = useTranslation(['requests']);
  const inactiveSuffix = t('requests:draft.dictionarySelections.inactiveSuffix');
  return (
    <div data-testid="request-dictionary-selections" style={{ display: 'grid', gap: 10 }}>
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--fg-3)',
            marginBottom: 4,
          }}
        >
          {t('requests:draft.roads.label')}
        </div>
        <ChipList
          items={roads}
          testId="request-roads"
          emptyLabel={t('requests:draft.roads.empty')}
          inactiveSuffix={inactiveSuffix}
        />
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--fg-3)',
            marginBottom: 4,
          }}
        >
          {t('requests:draft.packaging.label')}
        </div>
        <ChipList
          items={packaging}
          testId="request-packaging"
          emptyLabel={t('requests:draft.packaging.empty')}
          inactiveSuffix={inactiveSuffix}
        />
      </div>
    </div>
  );
}
